import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { css } from '@emotion/react';
import SignaturePad from 'signature_pad';
import { theme } from '@frontend/theme';
import { SIGNATURE_PAD_HEIGHT, SIGNATURE_PAD_MIN_HEIGHT, SIGNATURE_PAD_WIDTH } from './constants';
import { trimCanvas } from './signature-image';

export interface ExportedSignature {
  canvas: HTMLCanvasElement;
  /** Size in CSS pixels (the canvas itself is device-pixel-ratio scaled). */
  width: number;
  height: number;
}

export interface SignaturePadHandle {
  clear: () => void;
  isEmpty: () => boolean;
  /** Returns the drawing cropped to its strokes plus `padding` CSS px, or null when nothing is drawn. */
  exportTrimmed: (padding?: number) => ExportedSignature | null;
}

/** Always the standard signature pad size (see `SIGNATURE_PAD_*`): it shrinks with its container and keeps the ratio. */
export interface SignaturePadCanvasProps {
  /** Fixed stroke width; omit for velocity-based pen pressure. */
  penWidth?: number;
  disabled?: boolean;
  ariaLabel: string;
  onDrawingChange?: (hasDrawing: boolean) => void;
  className?: string;
}

const canvasStyle = css({
  display: 'block',
  width: '100%',
  maxWidth: SIGNATURE_PAD_WIDTH,
  height: 'auto',
  backgroundColor: theme.colors.white,
  border: `1px solid ${theme.colors.neutral20}`,
  borderRadius: theme.borderRadius.small,
  cursor: 'crosshair',
  // Keep the page from scrolling/zooming while drawing on touch devices.
  touchAction: 'none',
  userSelect: 'none',
});

export const SignaturePadCanvas = forwardRef<SignaturePadHandle, SignaturePadCanvasProps>(
  ({ penWidth, disabled, ariaLabel, onDrawingChange, className }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const padRef = useRef<SignaturePad | null>(null);
    const onDrawingChangeRef = useRef(onDrawingChange);
    onDrawingChangeRef.current = onDrawingChange;

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const pad = new SignaturePad(canvas, {
        penColor: '#000000',
        backgroundColor: 'rgba(0,0,0,0)',
        minWidth: penWidth ? penWidth / 2 : 0.8,
        maxWidth: penWidth ? penWidth / 2 : 2.6,
        canvasContextOptions: { willReadFrequently: true },
      });
      padRef.current = pad;
      const handleEndStroke = () => onDrawingChangeRef.current?.(!pad.isEmpty());
      pad.addEventListener('endStroke', handleEndStroke);

      // Size the backing store to CSS size × DPR so strokes stay sharp; keep strokes across resizes.
      let previousCssWidth: number | undefined;
      let previousCssHeight: number | undefined;
      const resize = () => {
        const cssWidth = canvas.offsetWidth;
        if (!cssWidth) return;
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        const cssHeight = Math.max((cssWidth * SIGNATURE_PAD_HEIGHT) / SIGNATURE_PAD_WIDTH, SIGNATURE_PAD_MIN_HEIGHT);
        const nextWidth = Math.round(cssWidth * ratio);
        const nextHeight = Math.round(cssHeight * ratio);
        const backingStoreChanged = canvas.width !== nextWidth || canvas.height !== nextHeight;
        const oldCssWidth = previousCssWidth;
        const oldCssHeight = previousCssHeight;
        const hasPreviousSize = oldCssWidth !== undefined && oldCssHeight !== undefined;
        const cssSizeChanged = hasPreviousSize && (oldCssWidth !== cssWidth || oldCssHeight !== cssHeight);
        if (hasPreviousSize && !backingStoreChanged && !cssSizeChanged) return;

        const strokes = pad.toData();
        const resizedStrokes =
          oldCssWidth !== undefined && oldCssHeight !== undefined && strokes.length
            ? strokes.map((stroke) => ({
                ...stroke,
                points: stroke.points.map((point) => ({
                  ...point,
                  x: point.x * (cssWidth / oldCssWidth),
                  y: point.y * (cssHeight / oldCssHeight),
                })),
              }))
            : strokes;
        if (backingStoreChanged) {
          canvas.width = nextWidth;
          canvas.height = nextHeight;
        }
        canvas.style.height = `${cssHeight}px`;
        if (backingStoreChanged) canvas.getContext('2d')?.scale(ratio, ratio);
        pad.clear();
        if (resizedStrokes.length) pad.fromData(resizedStrokes);
        previousCssWidth = cssWidth;
        previousCssHeight = cssHeight;
      };
      resize();
      const observer = new ResizeObserver(resize);
      observer.observe(canvas);

      return () => {
        observer.disconnect();
        pad.removeEventListener('endStroke', handleEndStroke);
        pad.off();
        padRef.current = null;
      };
    }, [penWidth]);

    useEffect(() => {
      const pad = padRef.current;
      if (!pad) return;
      if (disabled) pad.off();
      else pad.on();
    }, [disabled]);

    useImperativeHandle(
      ref,
      () => ({
        clear: () => {
          padRef.current?.clear();
          onDrawingChangeRef.current?.(false);
        },
        isEmpty: () => padRef.current?.isEmpty() ?? true,
        exportTrimmed: (padding = 8) => {
          const canvas = canvasRef.current;
          if (!canvas || !padRef.current || padRef.current.isEmpty()) return null;
          const ratio = canvas.offsetWidth ? canvas.width / canvas.offsetWidth : 1;
          const trimmed = trimCanvas(canvas, Math.round(padding * ratio));
          if (!trimmed) return null;
          return { canvas: trimmed.canvas, width: trimmed.width / ratio, height: trimmed.height / ratio };
        },
      }),
      []
    );

    return (
      <canvas
        ref={canvasRef}
        role='img'
        aria-label={ariaLabel}
        css={canvasStyle}
        className={className}
        data-testid='signature-pad-canvas'
      />
    );
  }
);
SignaturePadCanvas.displayName = 'SignaturePadCanvas';
